import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Pressable,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Gesture, GestureDetector, ScrollView } from "react-native-gesture-handler";
import { runOnJS } from "react-native-reanimated";
import { Ionicons } from "@expo/vector-icons";
import { useHousePickDistanceUnit } from "../../../hooks/useHousePickDistanceUnit";

const KM_PER_MI = 1.60934;

/**
 * Full-screen-ish overlay modal showing:
 * 1) the prompt that was sent to the model
 * 2) the raw response from the model
 *
 * Parents control visibility/content; this component owns collapsible state.
 */
function HousePickPromptInfoModal({
  isVisible,
  onClose,
  getPromptText,
  rawResponse,
  onRetry,
  matchScoring,
  onMatchScoringChange,
  onResetMatchScoring,
}) {
  const { unit: distanceUnit, setUnit: setDistanceUnit } = useHousePickDistanceUnit();
  const [promptCollapsed, setPromptCollapsed] = useState(true);
  const [rawCollapsed, setRawCollapsed] = useState(true);
  const [scoringCollapsed, setScoringCollapsed] = useState(false);

  // Reset to a consistent default each time the modal opens.
  useEffect(() => {
    if (!isVisible) return;
    setPromptCollapsed(true);
    setRawCollapsed(true);
    setScoringCollapsed(false);
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <View style={styles.inSheetOverlay}>
      <Pressable style={styles.inSheetBackdrop} onPress={onClose} />

      <View style={[styles.modalCard, styles.promptInfoModalCard]}>
        <View style={styles.promptInfoModalHeader}>
          <View style={styles.promptInfoModalTitleWrap}>
            <Text style={styles.modalTitle}>Prompt & Raw Response</Text>
          </View>
          <TouchableOpacity
            style={styles.promptInfoModalClose}
            onPress={onClose}
            accessibilityLabel="Close"
          >
            <Ionicons name="close" size={24} color="#6B4C3B" />
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.promptInfoModalScroll}
          contentContainerStyle={styles.promptInfoModalScrollContent}
          showsVerticalScrollIndicator
          keyboardShouldPersistTaps="handled"
        >
          <View style={[styles.collapsibleSection, styles.collapsibleSectionFirst]}>
            <TouchableOpacity
              style={styles.collapsibleHeader}
              onPress={() => setPromptCollapsed((prev) => !prev)}
            >
              <Text style={styles.resultsTitle}>Your Prompt</Text>
              <Ionicons
                name={promptCollapsed ? "chevron-down" : "chevron-up"}
                size={20}
                color="#6B4C3B"
              />
            </TouchableOpacity>

            {!promptCollapsed && (
              <View style={styles.sectionBody}>
                <Text style={styles.promptText} selectable>
                  {getPromptText?.() ?? ""}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.collapsibleSection}>
            <TouchableOpacity
              style={styles.collapsibleHeader}
              onPress={() => setScoringCollapsed((prev) => !prev)}
            >
              <Text style={styles.resultsTitle}>Match Scoring</Text>
              <Ionicons
                name={scoringCollapsed ? "chevron-down" : "chevron-up"}
                size={20}
                color="#6B4C3B"
              />
            </TouchableOpacity>

            {!scoringCollapsed && (
              <View style={styles.sectionBody}>
                <Text style={styles.scoringHint}>
                  Tune matching strictness for name/address and acceptance threshold. Toggle off to
                  exclude a rule from matching.
                </Text>
                <ScoringSliderRow
                  label="Name weight"
                  enabled={matchScoring?.useNameWeight !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useNameWeight: v })}
                  min={0}
                  max={1}
                  step={0.05}
                  value={matchScoring?.nameWeight ?? 0.68}
                  onChange={(v) => onMatchScoringChange?.({ nameWeight: v })}
                />
                <ScoringSliderRow
                  label="Distance weight"
                  enabled={matchScoring?.useProximityWeight !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useProximityWeight: v })}
                  min={0}
                  max={1}
                  step={0.05}
                  value={matchScoring?.proximityWeight ?? 0.1}
                  onChange={(v) => onMatchScoringChange?.({ proximityWeight: v })}
                />
                <ScoringSliderRow
                  label="Address weight"
                  enabled={matchScoring?.useAddressWeight !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useAddressWeight: v })}
                  min={0}
                  max={1}
                  step={0.05}
                  value={matchScoring?.addressWeight ?? 0.22}
                  onChange={(v) => onMatchScoringChange?.({ addressWeight: v })}
                />
                <ScoringSliderRow
                  label="Score threshold"
                  enabled={matchScoring?.useScoreThreshold !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useScoreThreshold: v })}
                  min={0.3}
                  max={0.95}
                  step={0.01}
                  value={matchScoring?.threshold ?? 0.58}
                  onChange={(v) => onMatchScoringChange?.({ threshold: v })}
                />
                <ScoringSliderRow
                  label="Min name similarity"
                  enabled={matchScoring?.useMinNameSimilarity !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useMinNameSimilarity: v })}
                  min={0.01}
                  max={0.95}
                  step={0.01}
                  value={matchScoring?.minNameSimilarity ?? 0.55}
                  onChange={(v) => onMatchScoringChange?.({ minNameSimilarity: v })}
                />
                <ScoringSliderRow
                  label="Min name shared tokens"
                  enabled={matchScoring?.useMinNameTokenIntersections !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useMinNameTokenIntersections: v })}
                  min={1}
                  max={8}
                  step={1}
                  value={matchScoring?.minNameTokenIntersections ?? 1}
                  onChange={(v) => onMatchScoringChange?.({ minNameTokenIntersections: v })}
                />
                <ScoringSliderRow
                  label="Min address similarity"
                  enabled={matchScoring?.useMinAddressSimilarity !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useMinAddressSimilarity: v })}
                  min={0.2}
                  max={0.95}
                  step={0.01}
                  value={matchScoring?.minAddressSimilarity ?? 0.42}
                  onChange={(v) => onMatchScoringChange?.({ minAddressSimilarity: v })}
                />
                <ScoringSliderRow
                  label="Min address shared tokens"
                  enabled={matchScoring?.useMinAddressTokenIntersections !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useMinAddressTokenIntersections: v })}
                  min={1}
                  max={8}
                  step={1}
                  value={matchScoring?.minAddressTokenIntersections ?? 2}
                  onChange={(v) => onMatchScoringChange?.({ minAddressTokenIntersections: v })}
                />
                <ScoringSliderRow
                  label={`Max distance from AI address (${distanceUnit})`}
                  enabled={matchScoring?.useMaxDistanceFromAiKm !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useMaxDistanceFromAiKm: v })}
                  min={distanceUnit === "mi" ? 0.1 : 0.2}
                  max={distanceUnit === "mi" ? 31 : 50}
                  step={distanceUnit === "mi" ? 0.25 : 0.5}
                  value={
                    distanceUnit === "mi"
                      ? (matchScoring?.maxDistanceFromAiKm ?? 3.21868) / KM_PER_MI
                      : (matchScoring?.maxDistanceFromAiKm ?? 3.21868)
                  }
                  onChange={(v) =>
                    onMatchScoringChange?.({
                      maxDistanceFromAiKm: distanceUnit === "mi" ? v * KM_PER_MI : v,
                    })
                  }
                  helper={
                    <DistanceUnitToggle
                      value={distanceUnit}
                      onChange={setDistanceUnit}
                    />
                  }
                />
                <ScoringToggleRow
                  label="Require same street number"
                  enabled={matchScoring?.useStreetNumberMismatchCheck !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useStreetNumberMismatchCheck: v })}
                />
                <ScoringToggleRow
                  label="Require street number on both addresses"
                  enabled={matchScoring?.useOneSidedStreetNumberCheck !== false}
                  onToggleEnabled={(v) => onMatchScoringChange?.({ useOneSidedStreetNumberCheck: v })}
                />
                <TouchableOpacity
                  style={styles.resetScoringButton}
                  onPress={onResetMatchScoring}
                  activeOpacity={0.85}
                >
                  <Text style={styles.resetScoringButtonText}>Reset scoring defaults</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          <View style={styles.collapsibleSection}>
            <TouchableOpacity
              style={styles.collapsibleHeader}
              onPress={() => setRawCollapsed((prev) => !prev)}
            >
              <Text style={styles.resultsTitle}>Raw Response</Text>
              <Ionicons
                name={rawCollapsed ? "chevron-down" : "chevron-up"}
                size={20}
                color="#6B4C3B"
              />
            </TouchableOpacity>

            {!rawCollapsed && (
              <View style={styles.sectionBody}>
                {rawResponse ? (
                  <Text style={styles.aiResponseText} selectable>
                    {rawResponse}
                  </Text>
                ) : (
                  <Text style={styles.aiLoadingText}>Loading...</Text>
                )}
              </View>
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

function ScoringSliderRow({
  label,
  enabled,
  onToggleEnabled,
  min,
  max,
  step,
  value,
  onChange,
  helper = null,
}) {
  return (
    <View style={styles.scoringSliderRow}>
      <View style={styles.scoringSliderRowHeader}>
        <Text
          style={[styles.scoringSliderRowLabel, !enabled && styles.scoringSliderRowLabelDisabled]}
          numberOfLines={2}
        >
          {label}
        </Text>
        <Switch
          value={enabled}
          onValueChange={onToggleEnabled}
          trackColor={{ false: "#E8DDD7", true: "#D7C5BC" }}
          thumbColor={enabled ? "#6B4C3B" : "#F4F1EE"}
          ios_backgroundColor="#E8DDD7"
          accessibilityLabel={`${label} enabled`}
        />
      </View>
      <SliderLikeControl
        label=""
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        disabled={!enabled}
        hideLabelRow
      />
      {helper}
    </View>
  );
}

function ScoringToggleRow({ label, enabled, onToggleEnabled }) {
  return (
    <View style={styles.scoringSliderRow}>
      <View style={styles.scoringSliderRowHeader}>
        <Text
          style={[styles.scoringSliderRowLabel, !enabled && styles.scoringSliderRowLabelDisabled]}
          numberOfLines={2}
        >
          {label}
        </Text>
        <Switch
          value={enabled}
          onValueChange={onToggleEnabled}
          trackColor={{ false: "#E8DDD7", true: "#D7C5BC" }}
          thumbColor={enabled ? "#6B4C3B" : "#F4F1EE"}
          ios_backgroundColor="#E8DDD7"
          accessibilityLabel={`${label} enabled`}
        />
      </View>
    </View>
  );
}

function DistanceUnitToggle({ value, onChange }) {
  return (
    <View style={styles.distanceUnitToggleRow}>
      <TouchableOpacity
        style={[
          styles.distanceUnitPill,
          value === "mi" && styles.distanceUnitPillActive,
        ]}
        onPress={() => onChange?.("mi")}
        activeOpacity={0.85}
      >
        <Text
          style={[
            styles.distanceUnitPillText,
            value === "mi" && styles.distanceUnitPillTextActive,
          ]}
        >
          mi
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.distanceUnitPill,
          value === "km" && styles.distanceUnitPillActive,
        ]}
        onPress={() => onChange?.("km")}
        activeOpacity={0.85}
      >
        <Text
          style={[
            styles.distanceUnitPillText,
            value === "km" && styles.distanceUnitPillTextActive,
          ]}
        >
          km
        </Text>
      </TouchableOpacity>
    </View>
  );
}

function SliderLikeControl({
  label,
  value,
  min,
  max,
  step,
  onChange,
  disabled = false,
  hideLabelRow = false,
}) {
  const safeStep = step > 0 ? step : 1;
  const [trackWidth, setTrackWidth] = useState(0);
  const trackWidthRef = useRef(0);
  const startRatioRef = useRef(0);
  const sliderStateRef = useRef({
    min,
    max,
    safeStep,
    valueRange: Math.max(max - min, safeStep),
    onChange,
    value,
  });

  useEffect(() => {
    sliderStateRef.current = {
      min,
      max,
      safeStep,
      valueRange: Math.max(max - min, safeStep),
      onChange,
      value,
    };
  }, [min, max, safeStep, onChange, value]);

  useEffect(() => {
    trackWidthRef.current = trackWidth;
  }, [trackWidth]);

  const rounded = Number(value.toFixed(safeStep >= 1 ? 0 : 2));
  const valueRange = Math.max(max - min, safeStep);
  const ratio = Math.min(1, Math.max(0, (value - min) / valueRange));

  const emitFromRatio = useCallback((nextRatio) => {
    const s = sliderStateRef.current;
    const clampedRatio = Math.min(1, Math.max(0, nextRatio));
    const raw = s.min + clampedRatio * s.valueRange;
    const snapped = Number(
      (Math.round((raw - s.min) / s.safeStep) * s.safeStep + s.min).toFixed(s.safeStep >= 1 ? 0 : 2)
    );
    s.onChange?.(snapped);
  }, []);

  const setFromTrackX = useCallback(
    (x) => {
      const w = trackWidthRef.current;
      if (!w) return;
      emitFromRatio(x / w);
    },
    [emitFromRatio]
  );

  const beginPanDrag = useCallback(() => {
    const s = sliderStateRef.current;
    const vr = Math.max(s.max - s.min, s.safeStep);
    startRatioRef.current = (s.value - s.min) / vr;
  }, []);

  const applyPanTranslation = useCallback(
    (tx) => {
      const w = trackWidthRef.current;
      if (!w) return;
      emitFromRatio(startRatioRef.current + tx / w);
    },
    [emitFromRatio]
  );

  const sliderGesture = useMemo(() => {
    const tap = Gesture.Tap()
      .enabled(!disabled)
      .maxDuration(250)
      .onEnd((e) => {
        const x = e.x;
        runOnJS(setFromTrackX)(x);
      });

    const pan = Gesture.Pan()
      .enabled(!disabled)
      .activeOffsetX([-8, 8])
      .failOffsetY([-12, 12])
      .onStart(() => {
        runOnJS(beginPanDrag)();
      })
      .onUpdate((e) => {
        runOnJS(applyPanTranslation)(e.translationX);
      });

    return Gesture.Race(tap, pan);
  }, [disabled, setFromTrackX, beginPanDrag, applyPanTranslation]);

  const handleNudge = (deltaSteps) => {
    if (disabled) return;
    const s = sliderStateRef.current;
    const raw = s.value + deltaSteps * s.safeStep;
    const snapped = Number(
      (Math.round((raw - s.min) / s.safeStep) * s.safeStep + s.min).toFixed(s.safeStep >= 1 ? 0 : 2)
    );
    s.onChange?.(snapped);
  };

  const displayLabel = label || "Value";

  return (
    <View style={[styles.sliderControl, disabled && styles.sliderControlDisabled]}>
      {!hideLabelRow && (
        <View style={styles.sliderControlHeader}>
          <Text style={styles.sliderLabel}>{label}</Text>
          <Text style={styles.sliderValue}>{rounded}</Text>
        </View>
      )}
      {hideLabelRow && (
        <View style={styles.sliderValueOnlyRow}>
          <Text style={styles.sliderValue}>{rounded}</Text>
        </View>
      )}
      <View style={styles.sliderRow}>
        <TouchableOpacity
          style={[styles.sliderAdjustButton, disabled && styles.sliderAdjustButtonDisabled]}
          onPress={() => handleNudge(-1)}
          disabled={disabled}
          accessibilityLabel={`Decrease ${displayLabel}`}
        >
          <Ionicons name="remove" size={14} color="#6B4C3B" />
        </TouchableOpacity>
        <GestureDetector gesture={sliderGesture}>
          <View
            style={[styles.sliderTrack, disabled && styles.sliderTrackDisabled]}
            onLayout={(evt) => {
              const w = evt.nativeEvent.layout.width;
              trackWidthRef.current = w;
              setTrackWidth(w);
            }}
          >
            <View style={[styles.sliderFill, { width: `${ratio * 100}%` }]} />
            <View style={[styles.sliderThumb, { left: `${ratio * 100}%` }]} />
          </View>
        </GestureDetector>
        <TouchableOpacity
          style={[styles.sliderAdjustButton, disabled && styles.sliderAdjustButtonDisabled]}
          onPress={() => handleNudge(1)}
          disabled={disabled}
          accessibilityLabel={`Increase ${displayLabel}`}
        >
          <Ionicons name="add" size={14} color="#6B4C3B" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  inSheetOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1200,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  inSheetBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.35)",
  },
  modalCard: {
    width: "100%",
    maxWidth: 360,
    maxHeight: "82%",
    borderRadius: 16,
    backgroundColor: "#F9F5F3",
    borderWidth: 2,
    borderColor: "#6B4C3B",
    padding: 16,
  },
  promptInfoModalCard: {
    maxHeight: "92%",
    height: "92%",
    flex: 1,
  },
  promptInfoModalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  promptInfoModalTitleWrap: {
    flex: 1,
  },
  promptInfoModalClose: {
    padding: 4,
  },
  promptInfoModalScroll: {
    flex: 1,
    minHeight: 0,
  },
  promptInfoModalScrollContent: {
    paddingBottom: 24,
  },
  modalTitle: {
    fontSize: 24,
    color: "#000000",
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
    marginBottom: 12,
  },
  resultsTitle: {
    fontSize: 24,
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_700Bold",
    textAlign: "center",
  },
  collapsibleSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#6B4C3B",
    marginBottom: 20,
  },
  collapsibleSectionFirst: {
    marginTop: 32,
  },
  collapsibleHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  sectionBody: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    paddingTop: 0,
    width: "100%",
  },
  promptText: {
    fontSize: 16,
    color: "#2A1A14",
    fontFamily: "AbhayaLibre_400Regular",
    lineHeight: 24,
  },
  aiResponseText: {
    fontSize: 16,
    color: "#2A1A14",
    fontFamily: "AbhayaLibre_400Regular",
    lineHeight: 24,
    textAlign: "left",
    width: "100%",
  },
  scoringHint: {
    fontSize: 13,
    color: "#7D695F",
    fontFamily: "AbhayaLibre_400Regular",
    lineHeight: 18,
    marginBottom: 8,
  },
  scoringSliderRow: {
    marginTop: 10,
    paddingBottom: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#E5D9D2",
  },
  scoringSliderRowHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4,
  },
  scoringSliderRowLabel: {
    flex: 1,
    fontSize: 14,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
  },
  scoringSliderRowLabelDisabled: {
    color: "#9C8A80",
  },
  sliderControl: {
    marginTop: 8,
  },
  distanceUnitToggleRow: {
    marginTop: 8,
    flexDirection: "row",
    alignSelf: "flex-end",
    gap: 8,
  },
  distanceUnitPill: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#D7C5BC",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  distanceUnitPillActive: {
    borderColor: "#6B4C3B",
    backgroundColor: "#F3E8E2",
  },
  distanceUnitPillText: {
    color: "#9C8A80",
    fontSize: 12,
    fontFamily: "AbhayaLibre_700Bold",
  },
  distanceUnitPillTextActive: {
    color: "#6B4C3B",
  },
  sliderControlDisabled: {
    opacity: 0.45,
  },
  sliderValueOnlyRow: {
    alignItems: "flex-end",
    marginBottom: 2,
  },
  sliderControlHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  sliderLabel: {
    fontSize: 14,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_700Bold",
  },
  sliderValue: {
    fontSize: 14,
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_400Regular",
  },
  sliderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  sliderTrack: {
    flex: 1,
    height: 12,
    borderRadius: 999,
    backgroundColor: "#E8DDD7",
    position: "relative",
    overflow: "visible",
  },
  sliderTrackDisabled: {
    opacity: 0.7,
  },
  sliderFill: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 999,
    backgroundColor: "#6B4C3B",
  },
  sliderThumb: {
    position: "absolute",
    top: -4,
    width: 20,
    height: 20,
    marginLeft: -10,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#6B4C3B",
    backgroundColor: "#FFFFFF",
  },
  sliderAdjustButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#D7C5BC",
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  },
  sliderAdjustButtonDisabled: {
    opacity: 0.5,
  },
  resetScoringButton: {
    marginTop: 12,
    alignSelf: "flex-start",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#6B4C3B",
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "#FFFFFF",
  },
  resetScoringButtonText: {
    color: "#6B4C3B",
    fontSize: 13,
    fontFamily: "AbhayaLibre_700Bold",
  },
  aiLoadingText: {
    fontSize: 16,
    color: "#6B4C3B",
    fontFamily: "AbhayaLibre_400Regular",
    marginTop: 12,
    textAlign: "center",
  },
  aiErrorText: {
    fontSize: 16,
    color: "#DC2626",
    fontFamily: "AbhayaLibre_400Regular",
    marginTop: 12,
    textAlign: "center",
  },
  retryButton: {
    marginTop: 16,
    borderRadius: 10,
    backgroundColor: "#6B4C3B",
    paddingVertical: 10,
    paddingHorizontal: 20,
    alignItems: "center",
  },
  retryButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold",
  },
});

export default HousePickPromptInfoModal;

